import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useToast } from '../../components/ui/ToastProvider'
import { defaultUploadsService, useUploadFlow } from './useUploads'
import type { UploadBatchState, UploadsService, UseUploadFlowResult } from './useUploads'

interface UploadFlowContextValue {
  readonly handleFilesSelected: UseUploadFlowResult['handleFilesSelected']
  readonly batchFor: (sessionId: string) => UploadBatchState | null
  readonly batchError: string | null
  /** Clears a batch error left behind by a PREVIOUS visit to an upload entry
   * point. Called on mount by each entry point (`UploadsPage`, `HomePage`):
   * this provider outlives them all, so without it a failed batch's message
   * would still be on screen after navigating away and back, with no upload
   * in flight to explain it. */
  readonly clearBatchError: () => void
}

const UploadFlowContext = createContext<UploadFlowContextValue | undefined>(undefined)

/**
 * UID-583 (ADR-0173 D2) — app-wide owner of the upload batch.
 *
 * ## Why this exists
 *
 * ADR-0173 D1 navigates to `/uploads/{sessionId}` as soon as the session is
 * created, which is BEFORE the presigned PUTs, the thumbnail-reuse check, the
 * background thumbnail renders and the bounded BOM-hierarchy capture. All of
 * that used to be owned by the page the user was leaving. Hosting the flow
 * here, above `<Routes>` and at the same tier as `AssignToProjectProvider`
 * (UID-509) and `ToastProvider`, is what makes the work survive that
 * navigation, and what lets `StagingPage` — a DIFFERENT route — observe the
 * batch it is waiting on.
 *
 * Leaving the flow on the page and relying on the orphaned async closure was
 * considered and rejected (ADR-0173 D2): the closure does keep running, but
 * every `setFiles`/`setBatch` it performs is dropped on an unmounted
 * component, so nothing on the destination route can see the batch — which is
 * the whole requirement.
 *
 * ## Shape
 *
 * Modeled on `AssignToProjectProvider`: owns state, exposes it through one
 * `use*` hook, takes its service as a provider-level DI prop. Unlike that
 * provider it renders no floating UI of its own — the batch is displayed by
 * whichever route is mounted (`StagingPage`'s analysing panel).
 *
 * `useToast` is read HERE rather than threaded from the pages, so the
 * `onNotice` seam has exactly one wiring instead of the two identical copies
 * `UploadsPage` and `HomePage` used to carry. Both passed
 * `(message, position) => showToast(message, 'info', position)`; that is
 * preserved verbatim.
 */
export function UploadFlowProvider({
  children,
  service = defaultUploadsService,
}: {
  readonly children: ReactNode
  readonly service?: UploadsService
}) {
  const { showToast } = useToast()
  const { handleFilesSelected, batchFor, batchError, clearBatchError } = useUploadFlow(service, {
    onNotice: (message, position) => showToast(message, 'info', position),
  })
  /**
   * Gate-5 W5 — MEMOIZED, and it matters more here than at a typical
   * provider. This one re-renders on every XHR progress event (`updateFile`
   * → `setFiles`), and everything below it is the app: `AuthProvider`,
   * `AppShell`, the sidebar, and the mounted route. A fresh `value` object
   * each render would push that whole tree through reconciliation dozens of
   * times per uploaded file — a real regression against the page-scoped state
   * this replaced, even though the currently-mounted route during an upload
   * happens to be the cheap `ProgressPanel`.
   *
   * `batchFor` is a `useCallback` in the hook and `clearBatchError` likewise,
   * so this only re-identifies when the batch or the error actually changes —
   * which is exactly when a consumer needs to re-read.
   */
  const value = useMemo(
    () => ({ handleFilesSelected, batchFor, batchError, clearBatchError }),
    [handleFilesSelected, batchFor, batchError, clearBatchError],
  )
  return <UploadFlowContext.Provider value={value}>{children}</UploadFlowContext.Provider>
}

/**
 * THROWS outside a provider, deliberately — the same posture as `useToast`.
 *
 * A silent fallback (a no-op `handleFilesSelected`, or a second local
 * `useUploadFlow` instance) would give a page a dropzone that appears to work
 * and uploads nothing observable, or two competing flow instances with
 * different batch state. A missing provider is a wiring bug and should fail
 * loudly in the test that forgot it.
 */
export function useUploadFlowContext(): UploadFlowContextValue {
  const context = useContext(UploadFlowContext)
  if (context === undefined) {
    throw new Error('useUploadFlowContext must be used within an <UploadFlowProvider>')
  }
  return context
}

/**
 * READ-ONLY observer: where `sessionId`'s upload batch has got to, or `null`.
 *
 * Separate from `useUploadFlowContext` — which THROWS — and the difference is
 * deliberate, not a convenience:
 *
 *   - An upload ENTRY POINT (`UploadsPage`, `HomePage`) cannot function
 *     without the provider. For those, a missing provider is a wiring bug and
 *     must fail loudly, which is what still catches a broken `App.tsx`.
 *   - `StagingPage` merely ASKS whether a batch is in flight. It is an
 *     ordinary route, reachable from a bookmark or a session card with no
 *     upload anywhere in the picture, so requiring the upload subsystem to be
 *     mounted just to render a session would couple two unrelated things.
 *
 * `null` is therefore a first-class, expected answer here and means exactly
 * one thing — "no batch of mine is running for this session" — whether that
 * is because the upload finished, because a different tab started it, or
 * because no provider is mounted at all. Every consumer must already handle
 * it (it is the answer for every bookmarked/resumed arrival), so there is no
 * behaviour that exists only in the no-provider case.
 */
export function useUploadBatch(sessionId: string): UploadBatchState | null {
  const context = useContext(UploadFlowContext)
  return context?.batchFor(sessionId) ?? null
}
